import {
  Cartesian3,
  Math as CesiumMath,
  Color,
  ScreenSpaceEventHandler,
  ScreenSpaceEventType,
  Cartographic,
  CallbackProperty,
  JulianDate,
  Entity,
  PropertyBag,
  BoundingSphere,
  Transforms,
  Matrix4,
} from "cesium";
import { createUniqueId, getUsernameAndUserIdFormat, showToast } from "../Utility";
import Type from "../Types";
import { disableCameraControls, restoreCameraControls } from "../MapUtility";

export const handleCuttingGeojsonLinesGlobal = (viewer, originalCameraControls, layerNames, mapObjects, setMapObjects, cutHistory, mapObjectsRef, options = {}, userData) => {
  const { onCutDone } = options;
  const modifier = getUsernameAndUserIdFormat(userData);

  const handler = new ScreenSpaceEventHandler(viewer.scene.canvas);

  // ---- Drawing state
  let isDrawing = false;
  let drawPoints = [];
  let tempEntity = null;
  let lastGoodEndPos = null;

  // --- camera lock tracking
  let cameraLocked = false;
  const lockCamera = () => {
    if (cameraLocked) return true;
    const isDis = disableCameraControls(viewer, originalCameraControls);
    if (!isDis) return false;
    cameraLocked = true;
    return true;
  };
  const unlockCamera = () => {
    if (!cameraLocked) return;
    try {
      restoreCameraControls(viewer, originalCameraControls);
    } catch {}
    cameraLocked = false;
  };

  // --- ESC cancel handler
  const onKey = (e) => {
    if (e.key === "Escape") hardCleanup();
  };

  // ───────────────────────── Utils ─────────────────────────
  const now = () => JulianDate.now();

  const removeFileExtension = (s = "") => s.replace(/\.[^/.]+$/, "");
  const cleanName = (s = "") =>
    removeFileExtension(String(s))
      .replace(/[\s\-_]/g, "")
      .toLowerCase();

  const isLaneletDS = (ds) => cleanName(ds?.layerName || "").includes("lanelet");
  const getPropsNow = (e) => e?.properties?.getValue?.(now()) || {};
  const isDef = (v) => v !== null && v !== undefined;

  // Safe dataSource enumeration
  const getAllDataSources = () => {
    try {
      const dsCol = viewer.dataSources;
      if (!dsCol) return [];
      if (typeof dsCol.get === "function" && typeof dsCol.length === "number") {
        const out = [];
        for (let i = 0; i < dsCol.length; i++) {
          try {
            const d = dsCol.get(i);
            if (d) out.push(d);
          } catch {}
        }
        if (out.length) return out;
      }
      if (Array.isArray(dsCol._dataSources)) return dsCol._dataSources;
    } catch {}
    return [];
  };

  const getMouseCartesian = (winPos) => {
    if (!winPos) return null;
    let c = viewer.scene.pickPositionSupported ? viewer.scene.pickPosition(winPos) : null;
    if (c) return c;
    c = viewer.camera.pickEllipsoid(winPos);
    if (c) return c;
    if (viewer.scene.globe) {
      const ray = viewer.camera.getPickRay(winPos);
      if (ray) {
        c = viewer.scene.globe.pick(ray, viewer.scene);
        if (c) return c;
      }
    }
    return null;
  };

  // world -> lon/lat (ignore height)
  const toLonLat = (cartesian) => {
    const c = Cartographic.fromCartesian(cartesian);
    return [CesiumMath.toDegrees(c.longitude), CesiumMath.toDegrees(c.latitude)];
  };

  // ───────────────────────── Constants (planar-only) ─────────────────────────
  const EPS_M = 0.02; // numeric epsilon (meters)
  const ON_SEG_PAD_M = 0.02; // on-segment padding (meters) reduced
  const CUT_EXTEND_M = 0.5; // max extension for short cut lines (meters)
  const DEDUPE_M = 0.05; // dedupe threshold (meters)
  const ANGLE_SIN_MIN = 0.15; // require crossing angle (sin) to avoid near-parallel detection

  // Build ENU transform at origin
  const makeLocalTransforms = (origin) => {
    if (!origin) return { enu: null, inv: null };
    try {
      const enu = Transforms.eastNorthUpToFixedFrame(origin);
      const inv = Matrix4.inverse(enu, new Matrix4());
      if (!inv) return { enu, inv: null };
      return { enu, inv };
    } catch {
      return { enu: null, inv: null };
    }
  };

  // project world cartesian to local XY (discard Z)
  const projectToLocalXY = (p, inv) => {
    if (!inv || !p) return null;
    const v = Matrix4.multiplyByPoint(inv, p, new Cartesian3());
    return { x: v.x, y: v.y };
  };

  // segment intersection in XY with padding and parallel guard
  const segSegIntersectXY = (a, b, c, d) => {
    const r = { x: b.x - a.x, y: b.y - a.y };
    const s = { x: d.x - c.x, y: d.y - c.y };
    const rxs = r.x * s.y - r.y * s.x;
    const qpxr = (c.x - a.x) * r.y - (c.y - a.y) * r.x;

    const rLen = Math.hypot(r.x, r.y) || EPS_M;
    const sLen = Math.hypot(s.x, s.y) || EPS_M;

    // quick reject near-parallel/colinear: require minimum crossing angle
    const sinAngle = Math.abs(rxs) / (rLen * sLen);
    if (sinAngle < ANGLE_SIN_MIN) return null;

    const padT = Math.min(ON_SEG_PAD_M / rLen, 0.05); // cap at 5% of segment param
    const padU = Math.min(ON_SEG_PAD_M / sLen, 0.05);

    // normal intersection
    const t = ((c.x - a.x) * s.y - (c.y - a.y) * s.x) / rxs;
    const u = ((c.x - a.x) * r.y - (c.y - a.y) * r.x) / rxs;

    if (t >= -padT && t <= 1 + padT && u >= -padU && u <= 1 + padU) {
      return { tOnC: u, pointLocal: { x: a.x + t * r.x, y: a.y + t * r.y } };
    }
    return null;
  };

  // extend local segment both ways
  const extendSegmentLocal = (a, b, L) => {
    const vx = b.x - a.x;
    const vy = b.y - a.y;
    const len = Math.hypot(vx, vy) || 1;
    const ux = vx / len,
      uy = vy / len;
    return [
      { x: a.x - ux * L, y: a.y - uy * L },
      { x: b.x + ux * L, y: b.y + uy * L },
    ];
  };

  // planar intersection using local XY only
  const intersectMetricWithT = (p1, p2, q1, q2) => {
    const midTarget = Cartesian3.multiplyByScalar(Cartesian3.add(q1, q2, new Cartesian3()), 0.5, new Cartesian3());
    const transforms = makeLocalTransforms(midTarget);
    const inv = transforms.inv;
    const enu = transforms.enu;
    if (!inv || !enu) return null;

    const A = projectToLocalXY(p1, inv);
    const B = projectToLocalXY(p2, inv);
    const C = projectToLocalXY(q1, inv);
    const D = projectToLocalXY(q2, inv);
    if (!A || !B || !C || !D) return null;

    // smart extension based on cut length to avoid creating spurious hits when drawing on top
    const cutLen = Math.hypot(B.x - A.x, B.y - A.y);
    let ext = CUT_EXTEND_M;
    if (cutLen > 0) ext = Math.min(CUT_EXTEND_M, Math.max(0.1, cutLen * 0.5));
    const [Ae, Be] = extendSegmentLocal(A, B, ext);

    const hit = segSegIntersectXY(Ae, Be, C, D);
    if (!hit) return null;

    // English comment: Recompute intersection parameter on the original (non-extended) AB segment.
    // This prevents accepting hits that only exist on the extended line.
    const r = { x: B.x - A.x, y: B.y - A.y };
    const rLenSq = r.x * r.x + r.y * r.y || EPS_M * EPS_M;

    // compute t on original AB by projecting hit.pointLocal onto AB
    const tOnAB = ((hit.pointLocal.x - A.x) * r.x + (hit.pointLocal.y - A.y) * r.y) / rLenSq;

    // allow a small param padding but do not accept large outside hits
    const padTOrig = Math.min(ON_SEG_PAD_M / Math.sqrt(rLenSq), 0.05);
    if (tOnAB < -padTOrig || tOnAB > 1 + padTOrig) {
      // intersection falls outside original AB (beyond allowed tiny pad)
      return null;
    }

    // clamp t to [0,1] for constructing exact point on original segment
    const tClampedOnAB = Math.max(0, Math.min(1, tOnAB));
    const pointLocalOnOriginal = { x: A.x + tClampedOnAB * r.x, y: A.y + tClampedOnAB * r.y };

    // convert to world using enu (same as before)
    const localPoint = new Cartesian3(pointLocalOnOriginal.x, pointLocalOnOriginal.y, 0);
    const worldPoint = Matrix4.multiplyByPoint(enu, localPoint, new Cartesian3());

    // compute robust u on target segment as before (keep uClamped logic)
    let u = hit.tOnC;
    if (!Number.isFinite(u)) {
      const qdx = D.x - C.x,
        qdy = D.y - C.y;
      if (Math.abs(qdx) >= Math.abs(qdy)) u = (pointLocalOnOriginal.x - C.x) / (qdx || EPS_M);
      else u = (pointLocalOnOriginal.y - C.y) / (qdy || EPS_M);
    }
    const uClamped = Math.max(0, Math.min(1, u));

    return { point: worldPoint, t: uClamped };
  };

  // planar distance using local ENU at midpoint
  const planarDistance = (a, b) => {
    const mid = Cartesian3.multiplyByScalar(Cartesian3.add(a, b, new Cartesian3()), 0.5, new Cartesian3());
    const t = makeLocalTransforms(mid);
    const inv = t.inv;
    if (!inv) return Cartesian3.distance(a, b);
    const A = projectToLocalXY(a, inv);
    const B = projectToLocalXY(b, inv);
    if (!A || !B) return Cartesian3.distance(a, b);
    return Math.hypot(A.x - B.x, A.y - B.y);
  };

  // distance from point to segment in XY
  const distPointToSegmentXY = (a, b, p) => {
    const mid = Cartesian3.multiplyByScalar(Cartesian3.add(a, b, new Cartesian3()), 0.5, new Cartesian3());
    const t = makeLocalTransforms(mid);
    const inv = t.inv;
    if (!inv) return Infinity;
    const A = projectToLocalXY(a, inv);
    const B = projectToLocalXY(b, inv);
    const P = projectToLocalXY(p, inv);
    if (!A || !B || !P) return Infinity;
    const abx = B.x - A.x,
      aby = B.y - A.y;
    const apx = P.x - A.x,
      apy = P.y - A.y;
    const denom = abx * abx + aby * aby || 1e-12;
    let tparam = (apx * abx + apy * aby) / denom;
    tparam = Math.max(0, Math.min(1, tparam));
    const closest = { x: A.x + tparam * abx, y: A.y + tparam * aby };
    return Math.hypot(P.x - closest.x, P.y - closest.y);
  };

  const pointToPolylineDistance = (p, positions) => {
    let d = Infinity;
    for (let i = 1; i < positions.length; i++) d = Math.min(d, distPointToSegmentXY(positions[i - 1], positions[i], p));
    return d;
  };

  const dedupeIntersections = (arr) => {
    const out = [];
    for (const it of arr) {
      const dup = out.find((o) => planarDistance(o.point, it.point) <= DEDUPE_M);
      if (!dup) out.push(it);
    }
    return out;
  };

  const findIntersectionPoints = (cutLine, targetPositions) => {
    const out = [];
    for (let i = 0; i < targetPositions.length - 1; i++) {
      const hit = intersectMetricWithT(cutLine[0], cutLine[1], targetPositions[i], targetPositions[i + 1]);
      if (hit) out.push({ index: i + 1, point: hit.point, t: hit.t });
    }
    out.sort((a, b) => a.index - b.index || a.t - b.t);
    return dedupeIntersections(out);
  };

  const splitPolylineAtIntersections = (polyline, intersections) => {
    if (!intersections?.length) return [polyline];
    const groups = new Map();
    intersections.forEach((it) => {
      if (!groups.has(it.index)) groups.set(it.index, []);
      groups.get(it.index).push(it);
    });
    const segments = [];
    let current = [];
    for (let i = 0; i < polyline.length; i++) {
      current.push(polyline[i]);
      const arr = groups.get(i + 1);
      if (arr?.length) {
        arr.sort((a, b) => a.t - b.t);
        for (const it of arr) {
          const last = current[current.length - 1];
          const same = planarDistance(last, it.point) <= EPS_M;
          if (!same) current.push(it.point);
          segments.push(current);
          current = [it.point];
        }
      }
    }
    if (current.length > 1) segments.push(current);
    return segments;
  };

  const getLength = (positions) => {
    if (!positions || positions.length < 2) return 0;
    let total = 0;
    for (let i = 1; i < positions.length; i++) total += planarDistance(positions[i - 1], positions[i]);
    return total;
  };

  // properties/id helpers
  const readNumericId = (e, key = "id") => {
    const v = getPropsNow(e)[key];
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };
  const getMaxPropertyIdInDataSource = (ds, key = "id") => {
    let maxId = -Infinity;
    (ds?.entities?.values || []).forEach((e) => {
      const n = readNumericId(e, key);
      if (Number.isFinite(n) && n > maxId) maxId = n;
    });
    return Number.isFinite(maxId) ? maxId : 0;
  };

  const isHelper = (e) => {
    const t = String(getPropsNow(e)?.type || "").toLowerCase();
    return t === "cut_line" || t === "cut_line_helper" || t.startsWith("cut_line");
  };

  const safeRemoveEntity = (entity) => {
    if (!entity) return;
    const col = entity.entityCollection;
    if (col?.contains?.(entity)) return void col.remove(entity);
    if (entity.id) {
      try {
        viewer.entities.removeById(entity.id);
      } catch {}
      const allDS = getAllDataSources();
      allDS.forEach((ds) => {
        try {
          ds.entities.removeById(entity.id);
        } catch {}
      });
    }
  };

  // ───────────────────────── Drawing handlers ─────────────────────────
  const startDrawing = (click) => {
    const isDis = lockCamera();
    if (!isDis) return;
    const pos = getMouseCartesian(click.position);
    if (!isDef(pos)) return;
    isDrawing = true;
    drawPoints = [pos];
    lastGoodEndPos = pos;
    tempEntity = viewer.entities.add({
      polyline: { positions: new CallbackProperty(() => drawPoints, false), width: 4, material: Color.RED.withAlpha(0.8), clampToGround: true },
      properties: new PropertyBag({ type: "cut_line_helper" }),
    });

    window.addEventListener("keydown", onKey);
  };

  const continueDrawing = (movement) => {
    if (!isDrawing) return;
    const pos = getMouseCartesian(movement.endPosition || movement.startPosition) || null;
    if (isDef(pos)) {
      drawPoints[1] = pos;
      lastGoodEndPos = pos;
    }
  };

  const endDrawing = (click) => {
    if (!isDrawing) return;
    isDrawing = false;
    const candidate = getMouseCartesian(click.position);
    const endPos = isDef(candidate) ? candidate : lastGoodEndPos;
    unlockCamera();
    try {
      window.removeEventListener("keydown", onKey);
    } catch {}
    if (!isDef(endPos)) {
      if (tempEntity) viewer.entities.remove(tempEntity);
      tempEntity = null;
      drawPoints = [];
      return;
    }
    drawPoints[1] = endPos;
    const fixedLine = [Cartesian3.clone(drawPoints[0]), Cartesian3.clone(drawPoints[1])];

    // length threshold (0.03 m)
    const lengthMeters = getLength(fixedLine);
    if (lengthMeters < 0.03) {
      try {
        showToast("The line must be at least 3 centimeters long", Type.NOTIFICATION.WARNING);
      } catch {}
      if (tempEntity) {
        try {
          viewer.entities.remove(tempEntity);
        } catch {}
        tempEntity = null;
      }
      drawPoints = [];
      lastGoodEndPos = null;
      return;
    }

    if (tempEntity) {
      viewer.entities.remove(tempEntity);
      tempEntity = null;
    }
    const cutLineEntity = addCutLineToDataSource(fixedLine);
    doCutting(fixedLine, cutLineEntity);
    drawPoints = [];
    lastGoodEndPos = null;
  };

  // cancel on right-click
  handler.setInputAction(() => {
    if (isDrawing) {
      isDrawing = false;
      if (tempEntity) {
        viewer.entities.remove(tempEntity);
        tempEntity = null;
      }
      drawPoints = [];
      lastGoodEndPos = null;
    }
    unlockCamera();
    try {
      window.removeEventListener("keydown", onKey);
    } catch {}
  }, ScreenSpaceEventType.RIGHT_DOWN);

  const markEditedIfNeeded = (ent) => {
    if (!mapObjectsRef?.current) return;
    const temp = [...mapObjectsRef.current];
    for (const obj of temp) {
      try {
        if (obj.dataSource?.entities?.contains(ent)) {
          obj.is_edited = true;
          break;
        }
      } catch {}
    }
    setMapObjects?.(temp);
  };

  // ───────────────────────── Store & render cut line ─────────────────────────
  const addCutLineToDataSource = (fixedLine) => {
    const allDS = getAllDataSources();
    const targetDS = allDS.find((ds) => cleanName(ds.layerName || "").includes("cutline"));
    const feature = {
      type: "Feature",
      geometry: { type: "LineString", coordinates: fixedLine.map(toLonLat) },
      properties: { atlas_id: typeof createUniqueId === "function" ? createUniqueId() : `atlas_${Date.now()}` },
    };

    const next = Array.isArray(mapObjects) ? [...mapObjects] : [];
    let found = false;
    for (const key in next) {
      const name = cleanName(next[key]?.file_name || "");
      if (name.includes("cutline")) {
        next[key].geojson.features.push(feature);
        found = true;
        break;
      }
    }
    if (!found) next.push({ file_name: "cutline.geojson", geojson: { type: "FeatureCollection", features: [feature] } });
    setMapObjects?.(next);

    const eidSuffix = typeof createUniqueId === "function" ? createUniqueId() : `${Date.now()}`;
       const creator = getUsernameAndUserIdFormat(userData);

    const cutLineEntity = new Entity({
      polyline: { positions: fixedLine, width: 4, material: Color.RED.withAlpha(0.8), clampToGround: true },
      properties: new PropertyBag({
        type: "cut_line",
        createdAt: new Date().toISOString(),
        ...(creator ? { creator } : {}),
      }),
      id: `cut_line_${eidSuffix}`,
    });

    if (targetDS) {
      try {
        targetDS.entities.add(cutLineEntity);
      } catch {
        viewer.entities.add(cutLineEntity);
      }
    } else viewer.entities.add(cutLineEntity);

    markEditedIfNeeded(cutLineEntity);
    return cutLineEntity;
  };

  const doCutting = (drawnLine, cutLineEntity) => {
    const historyEntry = { originalEntities: [], newEntities: [], dataSources: [], cutLineEntity: cutLineEntity || null };
    const allDS = getAllDataSources();
    const dsList = layerNames?.length
      ? allDS.filter((ds) => {
          const cn = cleanName(ds.layerName || "");
          return layerNames.some((ln) => cn.includes(cleanName(ln)));
        })
      : allDS;

    const N = now();
    const plans = [];
    dsList.forEach((ds) => {
      (ds.entities?.values || []).forEach((e) => {
        const pos = e.polyline?.positions?.getValue(N);
        if (pos?.length >= 2 && e.properties && !isHelper(e)) {
          const intersections = findIntersectionPoints(drawnLine, pos);
          const willCut = intersections.length > 0;
          const segments = willCut ? splitPolylineAtIntersections(pos, intersections) : null;
          const lengths = segments ? segments.map(getLength) : null;
          let longestIdx = 0;
          if (lengths && lengths.length > 1) {
            for (let i = 1; i < lengths.length; i++) if (lengths[i] > lengths[longestIdx]) longestIdx = i;
          }
          const originalProps = { ...getPropsNow(e) };
          const originalIdNum = Number(originalProps?.id);
          plans.push({
            entity: e,
            dataSource: ds,
            intersections,
            segments,
            lengths,
            longestIdx,
            willCut,
            originalProps,
            originalId: Number.isFinite(originalIdNum) ? originalIdNum : undefined,
            isLanelet: isLaneletDS(ds),
          });
        }
      });
    });

    // ID allocator
    const dsNextId = new Map();
    for (const ds of dsList) dsNextId.set(ds, getMaxPropertyIdInDataSource(ds, "id"));
    const getNextIdForDS = (ds, minimum = 0) => {
      const cur = dsNextId.get(ds) ?? 0;
      const base = Math.max(cur, minimum);
      const next = base + 2;
      dsNextId.set(ds, next);
      return next;
    };

    // index by id
    const plansById = new Map();
    for (const p of plans) {
      if (!Number.isFinite(p.originalId)) continue;
      if (!plansById.has(p.originalId)) plansById.set(p.originalId, []);
      plansById.get(p.originalId).push(p);
    }

    // lanelet grouping
    const grouped = [];
    const consumed = new Set();
    for (const p of plans) {
      if (!p.isLanelet || !p.willCut) continue;
      const leftId = Number(p.originalProps?.left_line_id);
      const rightId = Number(p.originalProps?.right_line_id);
      if (!Number.isFinite(leftId) || !Number.isFinite(rightId)) {
        p.willCut = false;
        continue;
      }
      const leftCandidates = plansById.get(leftId) || [];
      const rightCandidates = plansById.get(rightId) || [];
      const leftPlan = leftCandidates.find((q) => q.willCut);
      const rightPlan = rightCandidates.find((q) => q.willCut);
      if (leftPlan && rightPlan) {
        grouped.push({ lanelet: p, left: leftPlan, right: rightPlan });
        consumed.add(p);
        consumed.add(leftPlan);
        consumed.add(rightPlan);
      } else {
        p.willCut = false;
      }
    }

    const splitCache = new Map();
    const realizeSplit = (plan, options = {}) => {
      const { laneletBordersMeta = null } = options;
      const { entity, dataSource, segments, longestIdx, originalProps, originalId, isLanelet } = plan;
      if (!Number.isFinite(originalId)) {
        showToast("No valid 'id' on the line. Cutting skipped.", Type.NOTIFICATION.INFO);
        return { did: false, allMeta: [], smallIds: [] };
      }
      if (!segments || segments.length < 2) return { did: false, allMeta: [], smallIds: [] };

      dsNextId.set(dataSource, Math.max(dsNextId.get(dataSource) ?? 0, originalId));
      markEditedIfNeeded(entity);
      try {
        dataSource.entities.remove(entity);
      } catch {}
      historyEntry.originalEntities.push(entity);
      historyEntry.dataSources.push(dataSource);

      const smallIds = [];
      const allMeta = [];
      const pickByPolyline = (arr, center) => {
        if (!arr?.length) return null;
        let best = null,
          bestD = Infinity;
        for (const m of arr) {
          const d = pointToPolylineDistance(center, m.positions);
          if (d < bestD) {
            bestD = d;
            best = m;
          }
        }
        return best;
      };

      segments.forEach((segment, idx) => {
        const isLargest = idx === longestIdx;
        const newProps = { ...originalProps };
        if (modifier) newProps.modifier = modifier;
        let assignedId;
        if (isLargest) {
          assignedId = originalId;
          newProps.id = originalId;
        } else {
          assignedId = getNextIdForDS(dataSource, originalId);
          newProps.id = assignedId;
          smallIds.push(assignedId);
        }

        if (isLanelet && laneletBordersMeta) {
          const center = BoundingSphere.fromPoints(segment).center;
          const leftPick = pickByPolyline(laneletBordersMeta.leftAllMeta, center);
          const rightPick = pickByPolyline(laneletBordersMeta.rightAllMeta, center);
          if (leftPick) newProps.left_line_id = leftPick.id;
          if (rightPick) newProps.right_line_id = rightPick.id;
        }

        const newEntity = new Entity({
          polyline: { positions: segment, width: entity.polyline.width, material: entity.polyline.material, clampToGround: true, heightReference: entity.polyline.heightReference },
          geojsonType: entity.geojsonType,
          properties: new PropertyBag(newProps),
          id: `${entity.id}_split_${isLargest ? "main" : "small"}_${typeof createUniqueId === "function" ? createUniqueId() : Date.now()}`,
        });

        try {
          dataSource.entities.add(newEntity);
        } catch {
          try {
            viewer.entities.add(newEntity);
          } catch {}
        }
        historyEntry.newEntities.push(newEntity);
        allMeta.push({ id: assignedId, positions: segment, isLargest });
      });

      return { did: true, allMeta, smallIds };
    };

    const ensureSplit = (plan) => {
      const key = plan?.entity;
      if (!key) return { did: false, allMeta: [], smallIds: [] };
      if (splitCache.has(key)) return splitCache.get(key);
      const res = realizeSplit(plan);
      splitCache.set(key, res);
      return res;
    };

    for (const grp of grouped) {
      const { lanelet, left, right } = grp;
      const leftRes = ensureSplit(left);
      const rightRes = ensureSplit(right);
      realizeSplit(lanelet, { laneletBordersMeta: { leftAllMeta: leftRes.allMeta, rightAllMeta: rightRes.allMeta } });
    }

    let anySplit = grouped.length > 0;
    for (const p of plans) {
      if (!p.willCut) continue;
      if (consumed.has(p)) continue;
      const res = realizeSplit(p);
      anySplit = anySplit || res.did;
    }

    if (anySplit) {
      cutHistory?.current?.push(historyEntry);
      showToast("The selected line is sliced", Type.NOTIFICATION.INFO);
      // notify caller that a cut actually occurred
      try {
        if (typeof onCutDone === "function") onCutDone(true);
      } catch {}
    } else if (historyEntry.cutLineEntity) {
      safeRemoveEntity(historyEntry.cutLineEntity);
      try {
        if (typeof onCutDone === "function") onCutDone(false);
      } catch {}
    }
  };

  // bind events
  handler.setInputAction(startDrawing, ScreenSpaceEventType.LEFT_DOWN);
  handler.setInputAction(continueDrawing, ScreenSpaceEventType.MOUSE_MOVE);
  handler.setInputAction(endDrawing, ScreenSpaceEventType.LEFT_UP);

  // robust destroy
  const hardCleanup = () => {
    isDrawing = false;
    unlockCamera();
    try {
      window.removeEventListener("keydown", onKey);
    } catch {}
    if (tempEntity) {
      try {
        viewer.entities.remove(tempEntity);
      } catch {}
      tempEntity = null;
    }
    try {
      handler.removeInputAction(ScreenSpaceEventType.LEFT_DOWN);
      handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
      handler.removeInputAction(ScreenSpaceEventType.LEFT_UP);
      handler.removeInputAction(ScreenSpaceEventType.RIGHT_DOWN);
    } catch {}
    try {
      handler.destroy();
    } catch {}
    drawPoints = [];
    lastGoodEndPos = null;
  };

  return { handler, destroy: hardCleanup };
};

export const undoLastCut = (viewer, cutHistory, mapObjects, setMapObjects) => {
  if (!cutHistory?.current?.length) return;

  const lastCut = cutHistory.current.pop();

  // --- helpers (local) ---
  const removeFileExtension = (s = "") => s.replace(/\.[^/.]+$/, "");
  const dataSources = () => viewer?.dataSources?._dataSources || [];

  const hardRemoveByIdEverywhere = (id) => {
    if (!id) return;
    try {
      viewer.entities.removeById(id);
    } catch {}
    for (const ds of dataSources()) {
      try {
        ds.entities.removeById(id);
      } catch {}
    }
  };

  const safeRemoveEntity = (ent) => {
    if (!ent) return;
    try {
      if (ent?.entityCollection?.contains?.(ent)) {
        ent.entityCollection.remove(ent);
        return;
      }
    } catch {}

    hardRemoveByIdEverywhere(ent?.id);
  };

  const safeAddEntityToDS = (ds, entity) => {
    if (!entity) return;

    if (!ds?.entities?.add) {
      try {
        viewer.entities.add(entity);
      } catch {}
      return;
    }
    try {
      ds.entities.add(entity);
    } catch (e) {
      try {
        viewer.entities.add(entity);
      } catch {}
    }
  };

  // 1) Remove all split segments created by this cut
  for (const seg of lastCut?.newEntities || []) {
    safeRemoveEntity(seg);
  }

  // 1.5) Remove the red cut-line feature from external geojson store (if present)
  if (Array.isArray(mapObjects) && typeof setMapObjects === "function") {
    const next = mapObjects.slice();
    for (const obj of next) {
      const name = removeFileExtension(obj?.file_name || "")
        .replace(/[\s\-_]/g, "")
        .toLowerCase();
      if (name.includes("cutline")) {
        if (obj?.geojson?.features?.length) {
          obj.geojson.features.pop();
        }
        break;
      }
    }
    setMapObjects(next);
  }

  // 2) Restore original entities to their owning data sources
  const originals = lastCut?.originalEntities || [];
  const owners = lastCut?.dataSources || [];
  for (let i = 0; i < originals.length; i++) {
    const ent = originals[i];
    const ds = owners[i];
    safeAddEntityToDS(ds, ent);
  }

  // 3) Remove the red cut-line entity drawn for this cut
  if (lastCut?.cutLineEntity) {
    safeRemoveEntity(lastCut.cutLineEntity);
  }
};
